import React, { useEffect, useRef, useState } from 'react';
import { graphql, Link } from 'gatsby';
import { getImage, IGatsbyImageData } from 'gatsby-plugin-image';
import Chevron from 'assets/icons/chevron-right.svg';
import { Card, CardType } from 'components/people-card/card';
import { Layout } from 'components/layout';
import { BestOptionsList, parseProductProsCons, ProductType } from 'components/product-cards';
import { generateIndexedContent } from 'components/indexed-content/indexed-content';
import Indexes from 'components/indexed-content/indexes';
import { CiteType, GatsbyImageType, SocialMediaType } from 'types/common';
import { SourceCite } from 'components/source-cite';
import { Breadcrumb, Breadcrumbs } from 'components/breadcrumbs/breadcrumbs';
import 'styles/global.scss';
import {
  classnames,
  createAuthorsUrl,
  createReviewersUrl,
  createReviewUrl,
  createYoutubeUrl,
  getFullName,
  getVideoSrcDoc,
  getYoutubeId,
  isWindowDefined,
} from 'helpers/utils';
import { ContributorTypeEnum } from 'utils/enums';
import {
  ArticleSEO,
  BreadcrumbSEO,
  createArticlesJSONLD,
  createBreadCrumbJSONLD,
  createItemTypeJSONLD,
  createProductReviewJSONLD,
  getAbsoluteUrl,
  ProductSEO,
} from 'helpers/seo-helpers';
import { SEO } from 'components/seo';
import paths from 'constants/paths';
import * as styles from './blog-review.module.scss';

type BlogsReviewProps = {
  pageContext: {
    slug: string
    cites: CiteType[]
    content: {
      data: {
        content: string
      }
    }
  }
  data: {
    strapiReview: {
      id: string
      title: string
      introduction: string
      sponsoredText: {
        text: {
          data: {
            text: string
          }
        }
      }
      author: {
        nameFirst: string
        nameLast: string
        namePrefix: string
        nameSuffix: string
        slug: string
        shortDescription: string
        contributorType: ContributorTypeEnum
        avatar: GatsbyImageType
        socialMedia: SocialMediaType
        occupation: string
      }
      reviewer?: {
        nameFirst: string
        nameLast: string
        namePrefix: string
        nameSuffix: string
        socialMedia: SocialMediaType
        occupation: string
        slug: string
        contributorType: ContributorTypeEnum
        shortDescription: string
        avatar: {
          localFile: {
            childImageSharp: {
              gatsbyImageData: IGatsbyImageData
            }
          }
        }
      }
      bestProducts: ProductType[]
      productList: ProductType[]
      medicalReviewDate: string
      updatedAt: string
      sources?: {
        data: {
          sources?: string
        }
      }
      publishedDate: string
      wordCount: number
      primaryImage: GatsbyImageType
      description: string
      tags: {
        value: string
      }[]
    }
    strapiSitewideSchema: {
      worstRating: number
      bestRating: number
    }
  }
};

const BlogReview = ({ data, pageContext }: BlogsReviewProps) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const {
    title, introduction, author, reviewer, bestProducts, productList, sponsoredText,
    medicalReviewDate, updatedAt, sources,
  } = data.strapiReview;
  const { cites } = pageContext;
  const { content } = pageContext.content.data;
  const [curatedContent, setCuratedContent] = useState(content);
  const [showSources, setShowSources] = useState(false);

  const getTitle = (
    prefix: string | null,
    firstName: string,
    lastName: string,
    suffix: string | null,
  ) => {
    const namePrefix = prefix || '';
    const nameSuffix = suffix || '';
    return `${namePrefix} ${firstName} ${lastName} ${nameSuffix}`;
  };

  const formattedDate = new Date(updatedAt).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const bestProductsTitles = bestProducts.map((p) => p.name);
  const indexedContent = generateIndexedContent(
    { content, filterTopPicks: bestProductsTitles },
  );

  const handleIndexClick = (index: number) => {
    if (contentRef.current) {
      const targetElement = contentRef.current.querySelectorAll('h2')[index];

      if (targetElement) {
        targetElement.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };
  useEffect(() => {
    setCuratedContent(parseProductProsCons(content, productList));
  }, []);

  useEffect(() => {
    if (isWindowDefined) {
      const figures = Array.from(document.getElementsByTagName('figure'));
      if (figures && figures.length > 0) {
        figures.forEach((figure, index) => {
          const oembed = figure.querySelector('oembed');
          if (oembed) {
            const url = figure.querySelector('oembed')!.getAttribute('url')!;
            const iframe = document.createElement('iframe');
            const youtubeUrl = createYoutubeUrl(url);
            iframe.setAttribute('loading', 'lazy');
            iframe.srcdoc = getVideoSrcDoc(youtubeUrl, getYoutubeId(youtubeUrl));
            iframe.title = `Video ${index}`;
            figure.replaceWith(iframe);
          }
        });
      }
      const images = Array.from(document.getElementsByTagName('img'));
      if (images && images.length > 0) {
        images.forEach((img) => {
          img.loading = 'lazy';
        });
      }
    }
  }, [curatedContent]);

  const breadcrumbs: Breadcrumb[] = [
    { name: 'Home page', slug: '/' },
    { name: 'Reviews', slug: '/review' },
    { name: title, slug: '' },
  ];

  return (
    <Layout>
      <div className={styles.genericContainer}>
        <Breadcrumbs className={styles.breadCrumb} breadcrumbs={breadcrumbs} />
        <h1 className={styles.headingTitle}>{title}</h1>
        <Card
          key={author.nameLast}
          image={getImage(author.avatar.localFile)}
          fullName={getTitle(
            author.namePrefix,
            author.nameFirst,
            author.nameLast,
            author.nameSuffix,
          )}
          principalSlug={createAuthorsUrl(author.slug)}
          secondarySlug={reviewer ? createReviewersUrl(reviewer.slug) : undefined}
          reviewer={reviewer ? getTitle(
            reviewer.namePrefix,
            reviewer.nameFirst,
            reviewer.nameLast,
            reviewer.nameSuffix,
          ) : undefined}
          medicalReviewDate={medicalReviewDate}
          type={CardType.mixed}
          sources={!!(sources?.data?.sources || cites.length > 0)}
          className={styles.card}
        />
        {sponsoredText && (
          <div className={styles.sponsorCard}>
            <p dangerouslySetInnerHTML={{ __html: sponsoredText.text.data.text }} />
          </div>
        )}
        {introduction && <div className={styles.intro}>{introduction}</div>}
        {bestProducts && (
          <BestOptionsList
            products={bestProducts}
            indexedContent={indexedContent}
            onIndexClick={handleIndexClick}
          />
        )}
        <div className={styles.gridContent}>
          <div className={styles.mainContent}>
            <div
              ref={contentRef}
              className={styles.contentStyle}
              dangerouslySetInnerHTML={{ __html: curatedContent }}
            />
            {!!(sources || cites.length > 0) && (
              <div className={showSources ? '' : styles.closedSources}>
                <button
                  className={classnames(styles.sourceButton, styles.expandSectionButton, showSources ? styles.expanded : '')}
                  onClick={() => setShowSources(!showSources)}
                >
                  <h2 className={styles.sourcesTitle}>
                    Sources
                    <Chevron className={classnames(styles.sourcesTitleIcon, showSources ? styles.expanded : '')} />
                  </h2>
                </button>
                {showSources && (
                  <div className={styles.openedSources}>
                    <p className={styles.sorcesContent}>
                      teethtalkgirl content is medically reviewed and fact-checked by a
                      licensed dentist or medical doctor to ensure the information is factual,
                      current, and relevant.
                    </p>
                    <p className={styles.sorcesContent}>
                      Our medical affairs team works hard to ensure the accuracy and integrity
                      by cite from current scientific research, such as scholarly articles,
                      dentistry textbooks, government agencies, and medical journals. This also
                      includes information provided by the American Dental Association (ADA),
                      the American Association of Orthodontics
                      (AAO), and the American Academy of Pediatrics (AAP).
                    </p>
                    {sources?.data?.sources
                      ? (
                        <p
                          className={styles.sorcesContent}
                          dangerouslySetInnerHTML={{ __html: sources?.data.sources || '' }}
                        />
                      )
                      : cites.map((cite) => (
                        <SourceCite cite={cite} className={styles.sorcesContent} />
                      ))}
                  </div>
                )}
              </div>
            )}
            <Card
              key={`${author.nameLast}card`}
              image={getImage(author.avatar.localFile)}
              type={CardType.author}
              fullName={getTitle(
                author.namePrefix,
                author.nameFirst,
                author.nameLast,
                author.nameSuffix,
              )}
              principalSlug={createAuthorsUrl(author.slug)}
              shortBio={author.shortDescription}
              className={styles.card}
            />
            {reviewer && (
              <Card
                key={reviewer.nameLast}
                image={getImage(reviewer.avatar.localFile)}
                type={CardType.reviewer}
                fullName={getTitle(
                  reviewer.namePrefix,
                  reviewer.nameFirst,
                  reviewer.nameLast,
                  reviewer.nameSuffix,
                )}
                principalSlug={createReviewersUrl(reviewer.slug)}
                className={styles.card}
              />
            )}
            <div className={styles.lastUpdated}>
              <span>Last updated on</span>
              <span>{formattedDate}</span>
              <Link to="/advertising-policy">Here is our process</Link>
            </div>
          </div>
          <div className={styles.indexSection}>
            <Indexes title="Top picks" indexes={indexedContent} onIndexClick={handleIndexClick} />
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default BlogReview;

export const query = graphql`
  query ReviewsPage($slug: String) {
    strapiReview (slug: {eq: $slug}) {
      id
      title
      introduction
      sponsoredText {
        text {
          data {
            text
          }
        }
      }
      author {
        nameFirst
        nameLast
        namePrefix
        nameSuffix
        slug
        contributorType
        occupation
        shortDescription
        socialMedia {
          instagram
          linkedIn
          pinterest
          tiktok
          twitter
          youtube
          facebook
        }
        avatar {
          localFile {
            childImageSharp {
              gatsbyImageData
            }
          }
        }
      }
      reviewer {
        nameFirst
        nameLast
        namePrefix
        nameSuffix
        slug
        contributorType
        shortDescription
        socialMedia {
          instagram
          linkedIn
          pinterest
          tiktok
          twitter
          youtube
          facebook
        }
        occupation
        avatar {
          localFile {
            childImageSharp {
              gatsbyImageData
            }
          }
        }
      }
      description
      primaryImage {
        url
        localFile {
          childImageSharp {
            gatsbyImageData
          }
        }
      }
      bestProducts {
        id
        title
        name
        description
        shortDescription
        secondaryImage {
          url
          localFile {
            childImageSharp {
              gatsbyImageData
            }
          }
        }
        price
        site {
          link
          name
        }
        pros {
          item
          id
        }
        cons {
          item
          id
        }
      }
      productList {
        strapi_id
        id
        title
        name
        description
        shortDescription
        rating
        image {
          url
          localFile {
            childImageSharp {
              gatsbyImageData
            }
          }
        }
        secondaryImage {
          url
          localFile {
            childImageSharp {
              gatsbyImageData
            }
          }
        }
        price
        site {
          link
          name
        }
        pros {
          item
          id
        }
        cons {
          item
          id
        }
      }
      medicalReviewDate
      content {
        data {
          content
        }
      }
      updatedAt
      sources {
        data {
          sources
        }
      }
      wordCount
      publishedDate
      tags {
        value
      }
    }
    strapiSitewideSchema {
      worstRating
      bestRating
    }
  }
`;

export const Head = ({ data, pageContext }: BlogsReviewProps) => {
  const { strapiReview } = data;
  const { author } = strapiReview;
  const reviewUrl = createReviewUrl(pageContext.slug);
  const articleJSONLD: ArticleSEO = {
    image: strapiReview.primaryImage.url,
    datePublished: strapiReview.publishedDate,
    description: strapiReview.description,
    headline: strapiReview.title,
    keywords: strapiReview.tags.map((tag) => tag.value).join(','),
    url: getAbsoluteUrl(reviewUrl),
  };
  const fullName = getFullName(
    author.namePrefix,
    author.nameFirst,
    author.nameLast,
    author.nameSuffix,
  );

  const breadcrumbs: BreadcrumbSEO[] = [
    {
      position: 1,
      name: 'Teeth Talk Girl',
      slug: '',
    },
    {
      position: 2,
      name: 'All Reviews',
      slug: paths.reviews,
    },
    {
      position: 3,
      name: strapiReview.title,
      slug: reviewUrl,
    },
  ];

  const mapProductToSEO = (product: ProductType): ProductSEO => ({
    title: product.title,
    name: product.name,
    rating: product.rating,
    description: product.shortDescription,
    image: product.image.url,
    authorName: fullName,
    authorSlug: author.slug,
    worstRating: data.strapiSitewideSchema.worstRating,
    bestRating: data.strapiSitewideSchema.bestRating,
    positiveNotes: product.pros.map((pro, index) => createItemTypeJSONLD(pro, index)),
    negativeNotes: product.cons.map((con, index) => createItemTypeJSONLD(con, index)),
  });

  const allProductsSEO = strapiReview.productList.map(
    (product) => createProductReviewJSONLD(mapProductToSEO(product)),
  );

  const jsonLDArray = [
    createArticlesJSONLD(
      articleJSONLD,
      fullName,
      createAuthorsUrl(author.slug),
      author.socialMedia,
      author.avatar.url,
      pageContext.content.data.content,
      author.occupation,
      strapiReview.updatedAt,
      strapiReview.wordCount,
    ),
    createBreadCrumbJSONLD(breadcrumbs),
    ...allProductsSEO,
  ];

  return (
    <SEO
      title={strapiReview.title}
      description={strapiReview.description}
      ogImageUrl={strapiReview.primaryImage.url}
      ogSlug={reviewUrl}
      jsonLDArray={jsonLDArray}
    >
      <>
        <meta property="og:type" content="articles" />
        <meta property="article:published_time" content={strapiReview.publishedDate} />
        {strapiReview.updatedAt && <meta property="article:modified_time" content={strapiReview.updatedAt} /> }
        <meta property="article:author" content={fullName} />
        {strapiReview.tags && strapiReview.tags.map((tag) => (
          <meta key={tag.value} property="article:tag" content={tag.value} />
        ))}
      </>
    </SEO>
  );
};
